import { useState } from 'react'
import { useBrandColors } from '../../hooks/useBrandColors'
import { useBrand } from '../../context/BrandContext'

const today = new Date()
const getDays = (start, count) => Array.from({length: count}, (_,i) => {
  const d = new Date(start)
  d.setDate(d.getDate() + i)
  return d
})

const DAYS_TO_SHOW = 30
const days = getDays(today, DAYS_TO_SHOW)

const fmt = d => d.toLocaleDateString('en-US', {month:'short', day:'numeric'})
const fmtDay = d => ['S','M','T','W','T','F','S'][d.getDay()]

const buildings = [
  {
    id: 'obhr', name: 'On-Campus Housing (OBHR)', type: 'housing',
    rooms: [
      { id: 'obhr160', name: '160 OBHR', capacity: 5 },
      { id: 'obhr164', name: '164 OBHR', capacity: 3 },
    ]
  },
  {
    id: 'bevelander', name: 'Bevelander Cottage', type: 'housing',
    rooms: [
      { id: 'bev1', name: 'Bevelander 1', capacity: 1 },
      { id: 'bev2', name: 'Bevelander 2', capacity: 1 },
      { id: 'bev3', name: 'Bevelander 3', capacity: 1 },
    ]
  },
  {
    id: 'emery', name: 'Emery Dorm', type: 'housing',
    rooms: [
      { id: 'emery1',  name: 'Emery 1',  capacity: 1 },
      { id: 'emery2a', name: 'Emery 2A', capacity: 1 },
      { id: 'emery3a', name: 'Emery 3A — Community Room', capacity: 0, isCommon: true },
      { id: 'emery4a', name: 'Emery 4A', capacity: 1 },
      { id: 'emery5a', name: 'Emery 5A', capacity: 1 },
      { id: 'emery6a', name: 'Emery 6A', capacity: 1 },
    ]
  },
  {
    id: 'birch', name: 'Birch Dorm', type: 'housing',
    rooms: [{ id: 'birch', name: 'Birch Dorm', capacity: 16 }]
  },
  {
    id: 'eden', name: 'Eden Cottage', type: 'housing',
    rooms: [{ id: 'eden', name: 'Eden Cottage', capacity: 1 }]
  },
  {
    id: 'oakes', name: 'Oakes Dorm', type: 'housing',
    rooms: [{ id: 'oakes', name: 'Oakes Dorm', capacity: 6 }]
  },
  {
    id: 'conference', name: 'Conference Rooms', type: 'conference',
    rooms: [
      { id: 'conf_a', name: 'Conference Room A', capacity: 20 },
      { id: 'conf_b', name: 'Conference Room B', capacity: 8 },
    ]
  },
  {
    id: 'dining', name: 'Dining', type: 'dining',
    rooms: [{ id: 'dining', name: 'Dining Hall', capacity: 60 }]
  },
]

// Demo bookings
const bookings = [
  { id: 1, roomId: 'bev1', guest: 'Guibao Ke',               gender: 'M', start: 0, end: 18 },
  { id: 2, roomId: 'bev2', guest: 'Prithviraj Rajebhosale',  gender: 'M', start: 0, end: 8  },
  { id: 3, roomId: 'bev3', guest: 'Harish Krishna Nagarajan',gender: 'M', start: 0, end: 25 },
  { id: 4, roomId: 'emery1',  guest: 'Mahla Majidian Eidgahi', gender: 'F', start: 0, end: 29 },
  { id: 5, roomId: 'emery4a', guest: 'Pankaj Garg',            gender: 'M', start: 0, end: 20 },
  { id: 6, roomId: 'emery5a', guest: 'Josselin Bertrand',      gender: 'M', start: 0, end: 29 },
  { id: 7, roomId: 'emery6a', guest: 'Samidha Kulkarni',       gender: 'F', start: 0, end: 29 },
  { id: 8, roomId: 'eden',    guest: 'Dr. Sarah Chen',         gender: 'F', start: 3, end: 12 },
  { id: 9, roomId: 'conf_a',  guest: 'Marine Biology Symposium', gender: '', start: 5, end: 7  },
  { id:10, roomId: 'conf_b',  guest: 'Lab Safety Training',    gender: '', start: 2, end: 2  },
]

const pendingRequests = [
  { id: 101, guest: 'Dr. Amara Osei',    space: 'Eden Cottage',      arrival: 'May 15', departure: 'Jun 30', type: 'Visiting Scientist',  status: 'pending' },
  { id: 102, guest: 'Lena Fischer',      space: 'Bevelander 2',      arrival: 'May 10', departure: 'Aug 15', type: 'Graduate Student',    status: 'pending' },
  { id: 103, guest: 'Conference Room A', space: 'Conference Room A', arrival: 'May 8',  departure: 'May 8',  type: 'Seminar Speaker',     status: 'pending' },
]

const typeColor = { housing: '#2A9D8F', conference: '#4A6B8A', dining: '#B8734A' }

export default function AdminCampus() {
  const { accent, accentLight, accentBg } = useBrandColors()
  const { brand } = useBrand()
  const label = brand.campusLabel || 'Collective Campus'

  const [expanded, setExpanded] = useState({ bevelander: true, emery: true })
  const [view, setView] = useState('grid')
  const [requests, setRequests] = useState(pendingRequests)
  const [toast, setToast] = useState(null)
  const [hoveredBooking, setHoveredBooking] = useState(null)

  function toggleBuilding(id) {
    setExpanded(e => ({ ...e, [id]: !e[id] }))
  }

  function actRequest(id, approve) {
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status: approve ? 'approved' : 'denied' } : r))
    const r = requests.find(r => r.id === id)
    setToast(approve ? `${r.guest}'s booking confirmed.` : `${r.guest}'s request declined.`)
    setTimeout(() => setToast(null), 3000)
  }

  function getBookingsForRoom(roomId) {
    return bookings.filter(b => b.roomId === roomId)
  }

  function isBooked(roomId, dayIndex) {
    return bookings.some(b => b.roomId === roomId && dayIndex >= b.start && dayIndex <= b.end)
  }

  function getBookingAtDay(roomId, dayIndex) {
    return bookings.find(b => b.roomId === roomId && dayIndex >= b.start && dayIndex <= b.end)
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length

  return (
    <div style={{fontFamily:"'Montserrat',sans-serif"}}>
      <div style={{marginBottom:20,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
        <div>
          <h1 style={{fontFamily:'Georgia,serif',fontSize:'1.4rem',color:'#2B2B2B',fontWeight:600}}>{label} — Admin</h1>
          <p style={{fontSize:11,color:'#6B7D6B',marginTop:2}}>Manage spaces, bookings, and guest requests</p>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {pendingCount > 0 && (
            <span style={{padding:'4px 12px',borderRadius:20,fontSize:11,fontWeight:600,background:accentBg,color:accent,border:'1px solid rgba(184,115,74,0.2)'}}>
              {pendingCount} pending request{pendingCount!==1?'s':''}
            </span>
          )}
          {['grid','requests'].map(v => (
            <button key={v} onClick={() => setView(v)}
              style={{padding:'7px 16px',border:`1px solid ${view===v?accent:'#EDE6DA'}`,borderRadius:6,background:view===v?accent:'#fff',color:view===v?'#fff':'#4A4A4A',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>
              {v === 'grid' ? '📅 Booking Grid' : `📋 Requests ${pendingCount > 0 ? `(${pendingCount})` : ''}`}
            </button>
          ))}
        </div>
      </div>

      {toast && (
        <div style={{display:'flex',alignItems:'center',gap:8,background:'rgba(74,124,89,0.1)',border:'1px solid #4A7C59',borderRadius:8,padding:'10px 14px',marginBottom:14}}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="#4A7C59" strokeWidth="1.5"/><path d="M5 8l2 2 4-4" stroke="#4A7C59" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <span style={{fontSize:12,color:'#4A7C59',fontWeight:500}}>{toast}</span>
        </div>
      )}

      {view === 'grid' && (
        <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>

          {/* Legend */}
          <div style={{padding:'10px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',gap:16,alignItems:'center',flexWrap:'wrap'}}>
            <div style={{fontSize:10,fontWeight:600,color:'#6B7D6B',textTransform:'uppercase',letterSpacing:'0.08em'}}>Legend:</div>
            {[
              ['#4A7C59','Available'],
              ['#2A6B8A','Male Guest'],
              ['#A0392B','Female Guest'],
              ['#8A9E8A','Blocked / Common'],
              [accent,'Confirmed Booking'],
            ].map(([color, label]) => (
              <div key={label} style={{display:'flex',alignItems:'center',gap:5,fontSize:10,color:'#4A4A4A'}}>
                <div style={{width:14,height:14,borderRadius:3,background:color}}/>
                {label}
              </div>
            ))}
          </div>

          {/* Grid */}
          <div style={{overflowX:'auto'}}>
            <table style={{borderCollapse:'collapse',minWidth:'100%',fontSize:11}}>
              <thead>
                <tr style={{background:'#F7F2EB'}}>
                  <th style={{width:160,minWidth:160,padding:'8px 14px',textAlign:'left',fontSize:10,fontWeight:600,color:'#6B7D6B',borderBottom:'1px solid #EDE6DA',borderRight:'1px solid #EDE6DA',position:'sticky',left:0,background:'#F7F2EB',zIndex:2}}>
                    Space
                  </th>
                  {days.map((d,i) => (
                    <th key={i} style={{width:28,minWidth:28,padding:'4px 2px',textAlign:'center',borderBottom:'1px solid #EDE6DA',borderRight:'1px solid #F0EBE2'}}>
                      <div style={{fontSize:8,color:'#8A9E8A'}}>{fmtDay(d)}</div>
                      <div style={{fontSize:9,fontWeight:600,color:i===0?accent:'#4A4A4A'}}>{d.getDate()}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {buildings.map(building => (
                  <>
                    {/* Building header row */}
                    <tr key={`building-${building.id}`} style={{background:'#2B2B2B',cursor:'pointer'}} onClick={() => toggleBuilding(building.id)}>
                      <td style={{padding:'8px 14px',fontWeight:600,color:'#fff',fontSize:11,borderRight:'1px solid rgba(255,255,255,0.1)',position:'sticky',left:0,background:'#2B2B2B',zIndex:1,display:'flex',alignItems:'center',gap:6}}>
                        <span style={{fontSize:10,color:'rgba(255,255,255,0.5)'}}>{expanded[building.id] ? '▼' : '▶'}</span>
                        <span style={{color:typeColor[building.type]||'#fff'}}>●</span>
                        {building.name}
                      </td>
                      {days.map((_,i) => (
                        <td key={i} style={{background:'#2B2B2B',borderRight:'1px solid rgba(255,255,255,0.05)',borderBottom:'1px solid rgba(255,255,255,0.05)'}}/>
                      ))}
                    </tr>

                    {/* Room rows */}
                    {expanded[building.id] && building.rooms.map(room => (
                      <tr key={room.id} style={{borderBottom:'1px solid #EDE6DA'}}>
                        <td style={{padding:'6px 14px 6px 24px',color:'#4A4A4A',fontSize:11,borderRight:'1px solid #EDE6DA',position:'sticky',left:0,background:'#fff',zIndex:1,whiteSpace:'nowrap'}}>
                          {room.isCommon
                            ? <span style={{color:'#8A9E8A',fontStyle:'italic'}}>⚙ {room.name}</span>
                            : room.name
                          }
                          {room.capacity > 0 && <span style={{fontSize:9,color:'#8A9E8A',marginLeft:4}}>({room.capacity})</span>}
                        </td>
                        {days.map((_,i) => {
                          if (room.isCommon) return (
                            <td key={i} style={{background:'repeating-linear-gradient(45deg,#EDE6DA,#EDE6DA 3px,#F7F2EB 3px,#F7F2EB 8px)',borderRight:'1px solid #F0EBE2'}}/>
                          )
                          const booking = getBookingAtDay(room.id, i)
                          const booked = !!booking
                          const isStart = booking && booking.start === i
                          const isEnd = booking && booking.end === i
                          return (
                            <td key={i}
                              onMouseEnter={() => booking && setHoveredBooking({...booking, x: i})}
                              onMouseLeave={() => setHoveredBooking(null)}
                              style={{
                                background: booked
                                  ? booking.gender === 'M' ? '#2A6B8A'
                                  : booking.gender === 'F' ? '#A0392B'
                                  : accent
                                  : '#4A7C5922',
                                borderRight:'1px solid rgba(255,255,255,0.2)',
                                borderLeft: isStart ? '2px solid rgba(255,255,255,0.4)' : 'none',
                                borderRadius: isStart ? '3px 0 0 3px' : isEnd ? '0 3px 3px 0' : 'none',
                                cursor: booked ? 'pointer' : 'default',
                                position:'relative',
                              }}>
                              {isStart && booking.guest && (
                                <div style={{position:'absolute',left:4,top:'50%',transform:'translateY(-50%)',fontSize:8,color:'rgba(255,255,255,0.9)',whiteSpace:'nowrap',overflow:'hidden',maxWidth:80,fontWeight:500,pointerEvents:'none'}}>
                                  {booking.guest.split(' ')[0]}
                                </div>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {/* Date range label */}
          <div style={{padding:'8px 16px',borderTop:'1px solid #EDE6DA',fontSize:10,color:'#8A9E8A'}}>
            Showing {fmt(days[0])} — {fmt(days[days.length-1])} · {DAYS_TO_SHOW} days
          </div>
        </div>
      )}

      {view === 'requests' && (
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Booking Requests</div>
              <span style={{padding:'2px 8px',borderRadius:8,fontSize:10,fontWeight:600,background:accentBg,color:accent}}>{pendingCount} pending</span>
            </div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'#F7F2EB'}}>
                  {['Guest','Space','Arrival','Departure','Type',''].map(h => (
                    <th key={h} style={{textAlign:'left',padding:'8px 16px',fontSize:9,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,borderBottom:'1px solid #EDE6DA'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id} style={{borderBottom:'1px solid #EDE6DA',opacity:r.status!=='pending'?0.5:1}}>
                    <td style={{padding:'12px 16px',fontWeight:500,color:'#2B2B2B'}}>{r.guest}</td>
                    <td style={{padding:'12px 16px',color:'#4A4A4A'}}>{r.space}</td>
                    <td style={{padding:'12px 16px',color:'#4A4A4A'}}>{r.arrival}</td>
                    <td style={{padding:'12px 16px',color:'#4A4A4A'}}>{r.departure}</td>
                    <td style={{padding:'12px 16px'}}>
                      <span style={{fontSize:10,padding:'2px 8px',borderRadius:8,fontWeight:600,background:accent+'15',color:accent}}>{r.type}</span>
                    </td>
                    <td style={{padding:'12px 16px'}}>
                      {r.status === 'pending' ? (
                        <div style={{display:'flex',gap:6}}>
                          <button onClick={() => actRequest(r.id, true)} style={{padding:'4px 12px',background:'rgba(74,124,89,0.1)',border:'1px solid #4A7C59',borderRadius:4,color:'#4A7C59',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>Confirm ✓</button>
                          <button onClick={() => actRequest(r.id, false)} style={{padding:'4px 12px',background:'rgba(160,57,43,0.08)',border:'1px solid #A0392B',borderRadius:4,color:'#A0392B',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>Decline ✖</button>
                        </div>
                      ) : (
                        <span style={{fontSize:11,fontWeight:600,color:r.status==='approved'?'#4A7C59':'#A0392B'}}>{r.status==='approved'?'✓ Confirmed':'Declined'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}


